import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { Download, ExternalLink, ChevronLeft } from "lucide-react";
import { getReferralDetail, type ReferralDetail } from "@/lib/investor.functions";
import { DeckAnalysisView, ScoreHeader } from "@/components/deck-analysis-view";

// Rendered inside /investor SidebarLayout (which handles role guard + waitForSession).
// Deep-link auth: when signed out, the parent layout's useRoleGuard redirects to
// /login?next=<pathname>. The login page threads `next` into emailRedirectTo so
// the magic-link roundtrip lands the investor back on this exact URL.
export const Route = createFileRoute("/investor/referrals/$deckId")({
  head: () => ({
    meta: [
      { title: "Referred deck - DeckScore" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: ReferralDetailPage,
});

function ReferralDetailPage() {
  const { deckId } = Route.useParams();
  const runDetail = useServerFn(getReferralDetail);
  const [detail, setDetail] = useState<ReferralDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await runDetail({ data: { deckId } });
        if (!cancelled) setDetail(d);
      } catch (err) {
        if (!cancelled) {
          const msg = err instanceof Error ? err.message : "Failed to load";
          setError(msg === "Forbidden" ? "This referral isn't linked to your account." : msg);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [runDetail, deckId]);

  if (loading) return <p className="text-ink-secondary">Loading referral…</p>;
  if (error || !detail) {
    return (
      <div className="bg-surface border border-line rounded-2xl p-8 text-center">
        <h1 className="font-display text-[22px] font-semibold">Not available</h1>
        <p className="text-ink-secondary mt-2">{error ?? "We couldn't load this referral."}</p>
        <Link to="/investor" className="inline-flex mt-4 rounded-full border border-line px-4 py-2 text-[13px] hover:border-ink">
          Back to referrals
        </Link>
      </div>
    );
  }

  return (
    <>
      <div className="mb-6">
        <Link to="/investor" className="inline-flex items-center gap-1 text-[13px] text-ink-secondary hover:text-ink">
          <ChevronLeft className="w-4 h-4" /> Back to referrals
        </Link>
      </div>

      <ScoreHeader
        eyebrow="referred deck"
        title={detail.company}
        score={detail.score}
        actions={
          <>
            {detail.deck.pdf_url ? (
              <a
                href={detail.deck.pdf_url}
                target="_blank"
                rel="noopener"
                className="inline-flex items-center gap-1.5 rounded-full bg-accent text-accent-ink px-4 py-2 text-[13.5px] font-semibold hover:-translate-y-px transition"
              >
                <ExternalLink className="w-4 h-4" /> View original deck (PDF)
              </a>
            ) : null}
            {detail.one_pager.url ? (
              <a
                href={detail.one_pager.url}
                target="_blank"
                rel="noopener"
                className="inline-flex items-center gap-1.5 rounded-full border border-line px-4 py-2 text-[13.5px] hover:border-ink"
              >
                <Download className="w-4 h-4" /> Download one-pager
              </a>
            ) : null}
          </>
        }
      />

      <DeckAnalysisView score={detail.score} />
    </>
  );
}

